import { CostApiDto, PriceApiDto } from '@b2x/storefront-api-js-client/src';
import classnames from 'classnames';
import React from 'react';

import { AlertFromDto } from './AlertFromDto';
import { analytics } from './analytics/analytics';
import { useCartApi } from './api/useCartApi';
import { useAppContext, useAppStaticContext } from './AppContext';
import { Badge } from './Badge';
import { CartSkuProps } from './CartSku';
import { CartSkuBadges } from './CartSkuBadges';
import { Col } from './Col';
import { ConditionalWrapper } from './ConditionalWrapper';
import { appConfig } from './config';
import { CartSkuQuantityFormHelper } from './form/CartSkuQuantityForm';
import { Select } from './form/fields/Select';
import { FormGroup } from './form/FormGroup';
import { Div, H2, H3, Span, Ul } from './HTMLElement';
import { t } from './i18n/i18n';
import { ImageSwitcher } from './ImageSwitcher';
import { OffersList } from './OffersList';
import { Link } from './router/Link';
import { Row } from './Row';
import { SeparatedList } from './SeparatedList';
import { useModals } from './useModals';
import { formatCurrency, renderUI } from './util';

const ANNOUNCEMENT_CLEAR_MS = 1000;
const FOCUS_DELAY_MS = 100;

const useCartSkuAnnouncer = () => {
  const [announcement, setAnnouncement] = React.useState('');
  // raccoglie tutti i timeout pendenti per poterli cancellare all'unmount
  const timeoutsRef = React.useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const registerTimeout = React.useCallback((cb: () => void, delay: number) => {
    const id = setTimeout(cb, delay);
    timeoutsRef.current.push(id);
    return id;
  }, []);

  React.useEffect(
    () => () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    },
    [],
  );

  const announceAndManageFocus = React.useCallback(
    (message: string, nextFocusTarget: HTMLElement | null) => {
      setAnnouncement(message);

      if (nextFocusTarget) {
        registerTimeout(() => {
          nextFocusTarget.focus();
          nextFocusTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, FOCUS_DELAY_MS);
      }

      // pulisce il messaggio dopo un po' (la live region torna vuota)
      registerTimeout(() => {
        setAnnouncement('');
      }, ANNOUNCEMENT_CLEAR_MS);
    },
    [registerTimeout],
  );

  return { announceAndManageFocus, announcement };
};

export const CartSkuA = ({
  cartSku,
  compactLayout,
  hideProductName,
  hideSkuName,
  hideVariantAttributes,
  index,
  readOnly,
  showMeasurementSku,
  showProductDescriptionShort,
  temporaryPropToDisableProductPageLink,
  temporaryPropToDisableWishlistButton,
}: CartSkuProps) => {
  const { moveToWishlist, removeSku } = useCartApi();
  const { session } = useAppContext();
  const { announceAndManageFocus, announcement } = useCartSkuAnnouncer();

  const removeButtonRef = React.useRef<HTMLButtonElement>(null);
  const wishlistButtonRef = React.useRef<HTMLButtonElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const getNextFocusTarget = React.useCallback(() => {
    if (!containerRef.current) return null;

    // Cerca il prossimo cart-sku nella lista
    const nextCartSku =
      containerRef.current.parentElement?.nextElementSibling?.querySelector('[role="button"], button');
    if (nextCartSku) return nextCartSku as HTMLElement;

    // Se non c'è un prossimo, cerca il precedente
    const prevCartSku =
      containerRef.current.parentElement?.previousElementSibling?.querySelector('[role="button"], button');
    if (prevCartSku) return prevCartSku as HTMLElement;

    // Come fallback, cerca il container principale del carrello
    return (containerRef.current.closest('[data-cart-list]') ??
      document.querySelector('[data-cart-list], main')) as HTMLElement | null;
  }, []);

  const handleRemoveButtonClick = React.useCallback(() => {
    const nextFocusTarget = getNextFocusTarget();
    removeSku({ cartSku })
      .then(() => {
        announceAndManageFocus(
          t('cart.productTile.itemRemovedAnnouncement', {
            productName: cartSku.sku?.product?.name ?? t('misc.unknownItem'),
          }),
          nextFocusTarget,
        );
      })
      .catch((error) => {
        console.error("Errore nella rimozione dell'articolo:", error);
        removeButtonRef.current?.focus();
      });
  }, [cartSku, removeSku, getNextFocusTarget, announceAndManageFocus]);

  const { onAddToWishlistAsGuestButtonClick } = useAppStaticContext();

  const handleMoveToWishlistButtonClick = React.useCallback(() => {
    if (session?.userLogged) {
      const nextFocusTarget = getNextFocusTarget();
      moveToWishlist({ idCartSku: cartSku.id })
        .then((response) => {
          announceAndManageFocus(
            t('cart.productTile.itemMovedToWishlistAnnouncement', {
              productName: cartSku.sku?.product?.name ?? t('misc.unknownItem'),
            }),
            nextFocusTarget,
          );
          cartSku.sku?.product && analytics.events.addToWishlist(response.eventId, cartSku.sku.product, cartSku.sku);
        })
        .catch((error) => {
          console.error('Errore nello spostamento alla wishlist:', error);
          wishlistButtonRef.current?.focus();
        });
    } else {
      onAddToWishlistAsGuestButtonClick && onAddToWishlistAsGuestButtonClick();
    }
  }, [
    session?.userLogged,
    getNextFocusTarget,
    moveToWishlist,
    cartSku.id,
    cartSku.sku,
    onAddToWishlistAsGuestButtonClick,
    announceAndManageFocus,
  ]);

  const wishlistEnabled = React.useMemo(
    () => appConfig.enableWishlist && !temporaryPropToDisableWishlistButton,
    [temporaryPropToDisableWishlistButton],
  );

  const productPageLinkEnabled = React.useMemo(
    () => !readOnly && !temporaryPropToDisableProductPageLink,
    [readOnly, temporaryPropToDisableProductPageLink],
  );

  return renderUI({
    bs5: (
      <Div className="cart-sku" innerRef={containerRef}>
        <Div aria-atomic="true" aria-live="polite" className="sr-only">
          {announcement}
        </Div>
        <Row gap={0} key={cartSku.id} uiClassName={{ bs5: classnames('py-3', { 'border-top': index > 0 }) }}>
          <Col size={{ xs: 12 }} uiClassName={{ bs5: 'col-md' }}>
            <Div className="cart-sku-info" dynamicPadding={!compactLayout ? { end: 3 } : undefined}>
              <Row>
                {cartSku.sku?.product && cartSku.sku.image?.src && (
                  <Col size={'auto'}>
                    <ConditionalWrapper
                      condition={productPageLinkEnabled}
                      wrapper={<Link closeModal to={cartSku.sku.product.url} />}
                    >
                      <Div style={{ width: 90 }}>
                        <ImageSwitcher
                          alt={cartSku.sku.product.name}
                          aspectRatio={appConfig.productImageAspectRatio}
                          fluid
                          format={288}
                          src={cartSku.sku.image.src}
                        />
                      </Div>
                    </ConditionalWrapper>
                  </Col>
                )}
                <Col>
                  <Div>
                    {cartSku.sku?.product && (
                      <ConditionalWrapper
                        condition={productPageLinkEnabled}
                        wrapper={
                          <Link className="text-reset text-decoration-none" closeModal to={cartSku.sku.product.url} />
                        }
                      >
                        <Div dynamicMargin={{ bottom: 2 }}>
                          {!hideProductName && (
                            <H2 className="product-name h6" dynamicMargin={{ bottom: 0 }}>
                              {cartSku.sku.product.name}
                            </H2>
                          )}
                          {showMeasurementSku && cartSku.sku.measurement && (
                            <>
                              <Div className="small" marginY={1}>
                                {cartSku.sku.shape && <Span className="pe-1">{cartSku.sku.shape}</Span>}
                                {cartSku.sku.measurement.value && (
                                  <Span className="pe-1">{cartSku.sku.measurement.value}</Span>
                                )}
                                {cartSku.sku.measurement.measurementUnit && (
                                  <Span className="pe-1">{cartSku.sku.measurement.measurementUnit}</Span>
                                )}
                              </Div>
                            </>
                          )}
                          {showProductDescriptionShort && cartSku.sku.product.descriptionShort && (
                            <H3 className="product-description-short h6" dynamicMargin={{ bottom: 0 }}>
                              {cartSku.sku.product.descriptionShort}
                            </H3>
                          )}
                          {!hideSkuName && cartSku.sku.product.name !== cartSku.sku.name && (
                            <H3 className="product-sku-name h6" dynamicMargin={{ bottom: 0 }}>
                              {cartSku.sku.name}
                            </H3>
                          )}
                        </Div>
                      </ConditionalWrapper>
                    )}
                    {cartSku.assembledComponents && cartSku.assembledComponents.length > 0 && (
                      <>
                        <strong className="small">{t('cart.productTile.composedOf')}</strong>
                        <Ul
                          className="assembled-component-product-list"
                          margin={0}
                          padding={0}
                          style={{ listStyle: 'none' }}
                        >
                          {cartSku.assembledComponents.map((componentSku) => (
                            <li className="small" key={componentSku.id}>
                              {`${componentSku.quantity}x - ${componentSku.sku?.product?.name} ${componentSku.sku?.name}`}
                              {componentSku.alerts !== undefined && componentSku.alerts.length > 0 && (
                                <Div marginTop={2}>
                                  {componentSku.alerts.map((alert) => (
                                    <AlertFromDto alert={alert} key={alert.code} light noMargin small />
                                  ))}
                                </Div>
                              )}
                            </li>
                          ))}
                        </Ul>
                      </>
                    )}
                    <Ul
                      dynamicMargin={{ bottom: 0 }}
                      dynamicPadding={{ xs: 0 }}
                      style={{ listStyle: 'none' }}
                      uiClassName={{ bs5: 'text-dark' }}
                    >
                      {cartSku.sku?.product?.giftCard ? (
                        <>
                          {cartSku.sku.attributesMap && (
                            <li>
                              {t('misc.giftCard.value')}{' '}
                              {formatCurrency(parseInt(cartSku.sku.attributesMap['Gift_card_valore']?.value ?? '0'))}
                            </li>
                          )}
                          <li>
                            {t('misc.giftCard.sender.label')} {cartSku.giftCard?.senderName}
                          </li>
                          <li>
                            {t('misc.giftCard.receiver.label')} {cartSku.giftCard?.receiverName}
                          </li>
                          <li>
                            {t('misc.giftCard.receiver.mail')} {cartSku.giftCard?.receiverEmail}
                          </li>
                          {cartSku.giftCard?.message && (
                            <li>
                              <GiftCardMessage message={cartSku.giftCard.message} />
                            </li>
                          )}
                        </>
                      ) : (
                        <>
                          {!hideVariantAttributes &&
                            cartSku.sku?.attributes
                              ?.filter((attribute) => attribute.variant === true)
                              .map((attribute) => (
                                <li key={attribute.typeCode}>
                                  <small>
                                    {attribute.title}: {attribute.value}
                                  </small>
                                </li>
                              ))}
                          {!hideVariantAttributes &&
                            appConfig.productVariantAttributesCodes?.map(
                              (attributeCode) =>
                                cartSku.sku?.product?.attributes?.find(
                                  (attribute) => attribute.typeCode === attributeCode,
                                ) && (
                                  <li key={attributeCode}>
                                    <small>
                                      {
                                        cartSku.sku.product.attributes.find(
                                          (attribute) => attribute.typeCode === attributeCode,
                                        )?.title
                                      }
                                      :{' '}
                                      {
                                        cartSku.sku.product.attributes.find(
                                          (attribute) => attribute.typeCode === attributeCode,
                                        )?.value
                                      }
                                    </small>
                                  </li>
                                ),
                            )}
                          {compactLayout && (
                            <li>
                              <small>
                                {t('cart.quantity')}: {cartSku.quantity}
                              </small>
                            </li>
                          )}
                        </>
                      )}
                    </Ul>
                    {compactLayout && cartSku.price !== undefined && cartSku.cost !== undefined && (
                      <ItemCostBlock cost={cartSku.cost} price={cartSku.price} quantity={cartSku.quantity} />
                    )}
                    {cartSku.offers && <OffersList offers={cartSku.offers.filter((offer) => offer.showOnCartSku)} />}
                  </Div>
                </Col>
              </Row>
            </Div>
          </Col>
          {!compactLayout && (
            <Col size={wishlistEnabled ? { md: 'auto' } : { md: 4, xl: 3, xs: 12 }} style={{ minWidth: '240px' }}>
              <Div
                className="cart-sku-action"
                display={'flex'}
                flexDirection={'column'}
                gap={3}
                justifyContent={'spaceBetween'}
                paddingTop={{ md: 0, xs: 3 }}
                uiClassName={{ bs5: 'h-100' }}
              >
                <Div className={classnames({ 'pt-3': readOnly })}>
                  <Row>
                    {!cartSku.sku?.product?.giftCard && (
                      <Col size="auto">
                        {!readOnly ? (
                          <CartSkuQuantityFormHelper
                            availability={appConfig.productMaxQuantity}
                            cartSku={cartSku}
                            readonly={readOnly}
                          >
                            {({ fieldsHelper }) => (
                              <FormGroup {...fieldsHelper.quantity.formGroup} label={undefined} noMarginBottom>
                                <Select
                                  aria-label={t('accessibility.select.quantity', {
                                    productName: cartSku.sku?.product?.name,
                                  })}
                                  {...fieldsHelper.quantity.select}
                                />
                              </FormGroup>
                            )}
                          </CartSkuQuantityFormHelper>
                        ) : (
                          <div className="d-flex align-items-center justify-content-end">
                            <span>x{cartSku.quantity}</span>
                          </div>
                        )}
                      </Col>
                    )}
                    {cartSku.price !== undefined && cartSku.cost !== undefined && (
                      <Col className={'d-flex align-items-center justify-content-end'}>
                        <ItemCostBlock cost={cartSku.cost} price={cartSku.price} quantity={cartSku.quantity} />
                      </Col>
                    )}
                  </Row>
                </Div>
                <CartSkuBadges cartSku={cartSku}>
                  {({ cartSkuBadges }) =>
                    cartSkuBadges.length > 1 && (
                      <Div alignItems="end" className="cart-sku-badges" display="flex" flexDirection="column" gap={2}>
                        {cartSkuBadges.map((cartSkuBadge) => (
                          <Badge key={cartSkuBadge.className} {...cartSkuBadge} />
                        ))}
                      </Div>
                    )
                  }
                </CartSkuBadges>
                {!readOnly && (
                  <Div
                    display={'flex'}
                    dynamicMargin={{ top: 2 }}
                    justifyContent={'end'}
                    uiClassName={{ bs5: 'text-dark' }}
                  >
                    <SeparatedList
                      items={[
                        ...(wishlistEnabled
                          ? [
                              {
                                'aria-label': `Sposta ${cartSku.sku?.product?.name || 'questo articolo'} nella lista desideri`,
                                cta: handleMoveToWishlistButtonClick,
                                innerRef: wishlistButtonRef,
                                label: t('cart.productTile.sendToWishlist'),
                              },
                            ]
                          : []),
                        ...(cartSku.canRemove
                          ? [
                              {
                                'aria-label': `Rimuovi ${cartSku.sku?.product?.name || 'questo articolo'} dal carrello`,
                                cta: handleRemoveButtonClick,
                                innerRef: removeButtonRef,
                                label: t('cart.productTile.removeItem'),
                              },
                            ]
                          : []),
                      ]}
                      separator={<Div alignSelf="center" uiClassName={{ bs5: 'vr' }} />}
                      textUnderlined
                    />
                  </Div>
                )}
              </Div>
            </Col>
          )}
          {cartSku.alerts !== undefined && cartSku.alerts.length > 0 && (
            <Col size={12}>
              <Div dynamicMargin={{ top: 2 }}>
                {cartSku.alerts.map((alert) => (
                  <AlertFromDto alert={alert} key={alert.code} light noMargin small />
                ))}
              </Div>
            </Col>
          )}
        </Row>
      </Div>
    ),
  });
};

interface ItemCostBlockProps {
  cost: CostApiDto;
  price: PriceApiDto;
  quantity?: number;
  small?: boolean;
}

const ItemCostBlock = ({ cost, price, quantity = 1, small }: ItemCostBlockProps) => {
  const { enablePricesWithExplicitTaxes } = appConfig;

  let crossedOutValue = enablePricesWithExplicitTaxes
    ? (price.crossedOutValueTaxable ?? price.crossedOutValue)
    : price.crossedOutValue;
  const costValue = enablePricesWithExplicitTaxes ? (cost.valueTaxable ?? cost.value) : cost.value;

  if (price.value > cost.value) {
    crossedOutValue = price.value;
  }

  return renderUI({
    bs5: (
      <Div className="cart-sku-price text-end">
        {cost.value > 0 ? (
          <>
            {crossedOutValue && (
              <Span className={'crossed-out-price'} dynamicPadding={{ end: 2 }}>
                <del>{formatCurrency(crossedOutValue * quantity)}</del>
              </Span>
            )}
            <Span uiClassName={{ bs5: crossedOutValue !== undefined ? 'text-offer' : undefined }}>
              <strong>{formatCurrency(costValue * quantity)}</strong>
            </Span>
            {enablePricesWithExplicitTaxes && (
              <Div className="price-vat-included">
                {t('misc.priceVatIncluded', { price: formatCurrency(cost.value * quantity) })}
              </Div>
            )}
          </>
        ) : (
          <strong>{t('misc.giftProduct')}</strong>
        )}
      </Div>
    ),
  });
};

interface GiftCardMessageProps {
  message: string;
}

const GiftCardMessage = ({ message }: GiftCardMessageProps) => {
  const { showModal } = useModals();

  const handleShowMessageButtonClick = React.useCallback(
    () =>
      showModal({
        children: message,
        title: 'Il tuo messaggio',
      }),
    [message, showModal],
  );

  return (
    <span
      className="gift-card-show-message"
      onClick={handleShowMessageButtonClick}
      onKeyPress={handleShowMessageButtonClick}
      role={'button'}
      tabIndex={0}
    >
      {t('misc.giftCard.message')}
    </span>
  );
};
